import Select, { OptionProps } from 'react-select';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './FilterDrawer.css';

import { useFilterDrawerStore } from './useFilterDrawerStore';

interface OptionType {
  value: number;
  label: string;
}

const FilterDrawer = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'FilterDrawer' });

  const activityOptions = [
    {
      value: true,
      label: t('yes'),
    },
    {
      value: false,
      label: t('no'),
    },
  ];

  const Option = (props: OptionProps<any>) => {
    const { isSelected, isFocused, label, innerRef, innerProps } = props;

    let backgroundColor = 'white';
    let color = 'black';
    if (isSelected) {
      backgroundColor = '#d4b0f5';
      color = 'black';
    } else if (isFocused) {
      backgroundColor = '#f3eaff';
      color = 'black';
    }

    return (
      <div
        ref={innerRef}
        {...innerProps}
        className="d-flex my-1 rounded-4 align-items-center px-3 py-2"
        style={{
          cursor: 'pointer',
          width: '100%',
          backgroundColor,
          color,
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 20,
            height: 20,
            borderRadius: 6,
            border: `2px solid ${isSelected ? '#5f00cc' : '#ccc'}`,
            background: isSelected ? '#5f00cc' : '#fff',
            marginRight: 8,
            position: 'relative',
            transition: 'all 0.15s',
          }}
        >
          {isSelected && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              style={{
                position: 'absolute',
                top: 1,
                left: 1,
                display: 'block',
              }}
            >
              <polyline
                points="3,8 7,12 13,4"
                style={{
                  fill: 'none',
                  stroke: 'white',
                  strokeWidth: 2.5,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                }}
              />
            </svg>
          )}
        </span>
        <label className="form-check-label mb-0 w-100" style={{ color, cursor: 'pointer' }}>
          {label}
        </label>
      </div>
    );
  };

  const {
    isFilterDrawerOpen,
    closeFilterDrawer,
    markers,
    appliedClientFilter,
    setAppliedClientFilter,
    activityFilter,
    setActivityFilter,
  } = useFilterDrawerStore();

  const [options, setOptions] = useState<OptionType[]>([]);
  const [selected, setSelected] = useState<OptionType[]>([]);
  const [selectedActivityFilter, setSelectedActivityFilter] = useState<boolean | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const uniqueClients = [...new Set(markers.map(m => m.clientId))].map(id => ({
      value: id,
      label: markers.find(m => m.clientId === id)?.clientName || t('client', { id }),
    }));
    setOptions(uniqueClients);
  }, [markers, t]);

  useEffect(() => {
    setSelected(options.filter(opt => appliedClientFilter.includes(opt.value)));
    setSelectedActivityFilter(activityFilter);
  }, [appliedClientFilter, options, activityFilter]);

  if (!isFilterDrawerOpen) return null;

  const handleChange = (newSelected: readonly OptionType[]) => {
    setSelected(newSelected as OptionType[]);
  };

  const handleApplyFilters = (clientIds: number[], activity: boolean | null) => {
    setAppliedClientFilter(clientIds);
    setActivityFilter(activity);
    closeFilterDrawer();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="position-fixed"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040,
        }}
        onClick={closeFilterDrawer}
      />

      {/* Drawer */}
      <div
        className="position-fixed bg-white shadow"
        style={{
          top: 0,
          left: 0,
          bottom: 0,
          minWidth: isMobile ? '100%' : '50%',
          maxWidth: isMobile ? '100%' : '50%',
          zIndex: 1041,
        }}
      >
        <div className="d-flex flex-column p-4">
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="h4 fw-bold mb-0">{t('clientsFilter')}</h2>
              <button onClick={closeFilterDrawer} className="btn btn-link text-dark p-0">
                <i className="ri-close-line fs-4" />
              </button>
            </div>
            <div className="row">
              <div className="col-12">
                <h5 className="mx-3">{t('selectClientsToDisplay')}</h5>
                <Select
                  options={options}
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  value={selected}
                  components={{ Option }}
                  onChange={handleChange}
                  classNamePrefix="rs"
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      backgroundColor: 'white',
                      color: 'black',
                      border: state.isFocused || state.menuIsOpen ? '2px solid #bdbdbd' : '2px solid #6a37d4',
                      outline: 'none',
                      boxShadow: 'none',
                      padding: '0px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      minHeight: '48px',
                      borderRadius: 8,
                    }),
                    singleValue: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    placeholder: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    menu: provided => ({
                      ...provided,
                      backgroundColor: 'white',
                      zIndex: 9999,
                      outline: 'none',
                      border: '1px solid #6a37d4',
                      boxShadow: 'none',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#5f00cc' : state.isFocused ? '#d4b0f5' : 'white',
                      color: state.isSelected ? 'white' : state.isFocused ? 'white' : 'black',
                      cursor: 'pointer',
                    }),
                    multiValue: base => ({
                      ...base,
                      backgroundColor: '#5f00cc',
                      borderRadius: 6,
                      padding: '2px 6px',
                    }),
                    multiValueLabel: base => ({
                      ...base,
                      color: 'white',
                    }),
                    multiValueRemove: base => ({
                      ...base,
                      color: 'white',
                      ':hover': {
                        backgroundColor: '#d4b0f5',
                        color: '#5f00cc',
                      },
                    }),
                    dropdownIndicator: base => ({
                      ...base,
                      color: 'black',
                    }),
                  }}
                />
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-xl-6 col-12">
                <h5 className="mx-3">{t('displaySitesWithActivityOnly')}</h5>
                <Select
                  classNamePrefix="rs"
                  value={activityOptions.find(opt => opt.value === selectedActivityFilter) || activityOptions[1]}
                  options={activityOptions}
                  onChange={option => setSelectedActivityFilter(option?.value ?? null)}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      backgroundColor: 'white',
                      color: 'black',
                      border: state.isFocused || state.menuIsOpen ? '2px solid #bdbdbd' : '2px solid #6a37d4',
                      outline: 'none',
                      boxShadow: 'none',
                      padding: '0px',
                      fontSize: '12px',
                      minHeight: '48px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      borderRadius: 8,
                    }),
                    singleValue: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    placeholder: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    menu: provided => ({
                      ...provided,
                      backgroundColor: 'white',
                      zIndex: 9999,
                      outline: 'none',
                      border: '1px solid #6a37d4',
                      boxShadow: 'none',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#5f00cc' : state.isFocused ? '#d4b0f5' : 'white',
                      color: state.isSelected ? 'white' : state.isFocused ? 'white' : 'black',
                      cursor: 'pointer',
                    }),
                    dropdownIndicator: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                  }}
                />
                <div className="text-muted mt-2 mx-1">
                  <small dangerouslySetInnerHTML={{ __html: t('activityInfo') }} />
                </div>
              </div>
              <div className="col-xl-6 col-12">
                <h5 className="mx-3">{t('displaySitesWithPCC')}</h5>
                <Select
                  classNamePrefix="rs"
                  defaultValue={activityOptions[1]}
                  options={activityOptions}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      backgroundColor: 'white',
                      color: 'black',
                      border: state.isFocused || state.menuIsOpen ? '2px solid #bdbdbd' : '2px solid #6a37d4',
                      outline: 'none',
                      boxShadow: 'none',
                      padding: '0px',
                      fontSize: '12px',
                      minHeight: '48px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      borderRadius: 8,
                    }),
                    singleValue: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    placeholder: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                    menu: provided => ({
                      ...provided,
                      backgroundColor: 'white',
                      zIndex: 9999,
                      outline: 'none',
                      border: '1px solid #6a37d4',
                      boxShadow: 'none',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#5f00cc' : state.isFocused ? '#d4b0f5' : 'white',
                      color: state.isSelected ? 'white' : state.isFocused ? 'white' : 'black',
                      cursor: 'pointer',
                    }),
                    dropdownIndicator: provided => ({
                      ...provided,
                      color: 'black',
                    }),
                  }}
                />
                <div className="text-muted mt-2 mx-1">
                  <small dangerouslySetInnerHTML={{ __html: t('pccInfo') }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-5 p-4">
          <button
            onClick={() => {
              setSelected([]);
              setSelectedActivityFilter(null);
              handleApplyFilters([], null);
            }}
            className="btn btn-ghost-danger me-2"
          >
            {t('clearFilters')}
          </button>
          <button
            onClick={() =>
              handleApplyFilters(
                selected.map(item => item.value),
                selectedActivityFilter,
              )
            }
            className="btn btn-ghost-success"
          >
            {t('applyFilters')}
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterDrawer;
